import React__default from "react";
import { Anchor } from "../../typography/anchor/anchor.mjs";
import { CarouselHero } from "../../carousel/carousel-hero/carousel-hero/carousel-hero.mjs";
import { CarouselHeroItem } from "../../carousel/carousel-hero/carousel-hero-item/carousel-hero-item.mjs";
import { getBackgroundColorClass } from "../../colors/index.mjs";
import debounce from "debounce";
import "tabbable";
import { useResponsiveProps } from "../../helpers/helper/hooks/use-responsive-props.mjs";
import { BannerItem } from "../banner-item/banner-item.mjs";
const BannerContext = React__default.createContext({
  carousel: false,
  highestElementHeight: 0
});
const BannerResponsive = (props) => {
  const { responsive, children, ...rest } = props;
  const responsiveProps = useResponsiveProps({
    responsive
  });
  return /* @__PURE__ */ React__default.createElement(BannerInner, { ...rest, ...responsiveProps }, children);
};
const BannerInner = (props) => {
  const [carouselItems, setCarouselItems] = React__default.useState([]);
  const { highestElementHeight } = React__default.useContext(BannerContext);
  const { align, background, children, className, margin, minHeight, noRadius, size = "small", url, variant, pauseButton, fullSizeImage } = props;
  React__default.useEffect(() => {
    if (children) {
      const items = [];
      React__default.Children.forEach(children, (child, index) => {
        if (React__default.isValidElement(child) && (child.type === BannerItem || child.type === Banner)) {
          const item = {
            key: index,
            image: child.props.image,
            children: child,
            id: child.props.id
          };
          items.push(item);
        }
      });
      setCarouselItems(items);
    }
  }, [children]);
  const getBackground = () => {
    if (background) {
      return background;
    }
    if (variant === "light") {
      return "neutral-bg-level-1";
    }
    return "neutral-bg-level-2";
  };
  const backgroundColor = getBackground();
  const BEM = () => {
    const classArray = ["banner", "banner--".concat(size)];
    if (backgroundColor) {
      classArray.push(getBackgroundColorClass(backgroundColor));
    }
    if (className) {
      classArray.push(className);
    }
    if (fullSizeImage && React__default.Children.count(children) === 1) {
      classArray.push("banner--full-size-image");
    }
    if (align) {
      classArray.push("banner--align-".concat(align));
    }
    if (url) {
      classArray.push("banner--anchor");
    }
    if (noRadius) {
      classArray.push("banner--no-radius");
    }
    return classArray.join(" ");
  };
  const marginBEM = (extraClass) => {
    const classArray = [extraClass];
    const addMarginClass = (direction, size2) => {
      if (size2) {
        return classArray.push("banner--margin-".concat(direction, "-").concat(size2));
      }
    };
    if (margin) {
      if (typeof margin === "string") {
        classArray.push("banner--margin-".concat(margin));
      } else {
        Object.keys(margin).forEach((key) => {
          const direction = key;
          addMarginClass(direction, margin[direction]);
        });
      }
    }
    return classArray.join(" ");
  };
  const CSS = () => {
    const styleEntities = {};
    if (minHeight) {
      styleEntities.minHeight = "".concat(minHeight, "px");
    }
    return styleEntities;
  };
  if (url) {
    const { ...anchorProps } = props;
    return /* @__PURE__ */ React__default.createElement(Anchor, { className: "".concat(BEM(), " ").concat(marginBEM()), attributes: { style: CSS() }, url, isUnderlined: anchorProps.isUnderlined || false, ...anchorProps }, children);
  }
  if (React__default.Children.count(children) > 1) {
    return /* @__PURE__ */ React__default.createElement(
      CarouselHero,
      {
        className: BEM(),
        containerClassName: marginBEM("carousel-hero__banner-container"),
        minHeight: minHeight ? minHeight : highestElementHeight,
        pauseButton,
        "data-theme": variant
      },
      carouselItems.map((item) => /* @__PURE__ */ React__default.createElement(CarouselHeroItem, { key: item.key, image: item.image, background: getBackground(), theme: props.variant }, item.children))
    );
  }
  return /* @__PURE__ */ React__default.createElement("div", { className: "".concat(BEM(), " ").concat(marginBEM()), style: CSS(), "data-theme": variant }, children);
};
const Banner = (props) => {
  const [carousel, setCarousel] = React__default.useState(false);
  const [highestElementHeight, setHighestElementHeight] = React__default.useState(0);
  const { responsive, children, ...rest } = props;
  React__default.useEffect(() => {
    if (React__default.Children.count(children) > 1) {
      setCarousel(true);
    }
    const handleResize = () => {
      const newElementHeights = [];
      if (children) {
        React__default.Children.forEach(children, (child) => {
          if (React__default.isValidElement(child) && (child.type === BannerItem || child.type === Banner)) {
            const element = document.getElementById(child.props.id);
            const elementHeight = (element == null ? void 0 : element.offsetHeight) || 0;
            newElementHeights.push(elementHeight);
          }
        });
      }
      if (newElementHeights.length > 0) {
        setHighestElementHeight(Math.max(...newElementHeights));
      }
    };
    const debouncedResizeHandler = debounce(handleResize, 100);
    window.addEventListener("resize", debouncedResizeHandler);
    window.addEventListener("load", debouncedResizeHandler);
    if (document.readyState === "complete") {
      debouncedResizeHandler();
    }
    return () => {
      window.removeEventListener("resize", debouncedResizeHandler);
      debouncedResizeHandler.clear();
    };
  }, [children]);
  return /* @__PURE__ */ React__default.createElement(BannerContext.Provider, { value: { carousel, highestElementHeight } }, responsive ? /* @__PURE__ */ React__default.createElement(BannerResponsive, { ...rest, responsive }, children) : /* @__PURE__ */ React__default.createElement(BannerInner, { ...rest }, children));
};
export {
  Banner,
  BannerContext
};
